import React, { useState, useEffect, useCallback, useRef } from 'react';
import StatusBadge from './StatusBadge';
import InvoiceLineItems from './InvoiceLineItems';
import { formatCurrency, formatDateTime } from '../../utils/invoiceFormatters';
import invoiceService from '../../services/invoiceService';

const STATUS_TABS = ['All', 'DRAFT', 'GENERATED', 'SENT', 'PAID'];
const PAGE_SIZE = 20;

const EnterpriseTable = ({ month, year, onRefresh, source = 'ECMS' }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [markingId, setMarkingId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const searchTimer = useRef(null);

  // Bulk action config per tab
  const BULK_ACTION = {
    DRAFT:     { label: 'Mark All as Generated', fromStatus: 'DRAFT',     toStatus: 'GENERATED', color: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'  },
    GENERATED: { label: 'Mark All as Sent',      fromStatus: 'GENERATED', toStatus: 'SENT',      color: 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'     },
    SENT:      { label: 'Mark All as Paid',       fromStatus: 'SENT',      toStatus: 'PAID',      color: 'bg-green-600 hover:bg-green-700 text-white border-green-600'  },
  };
  const bulkAction = BULK_ACTION[activeTab] || null;

  const fetchInvoices = useCallback(async (searchVal, tabVal, pageVal) => {
    if (!month || !year) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        month,
        year,
        source,
        page: pageVal,
        size: PAGE_SIZE,
      };
      if (tabVal && tabVal !== 'All') params.status = tabVal;
      if (searchVal && searchVal.trim()) params.search = searchVal.trim();

      const res = await invoiceService.getInvoices(params);
      setInvoices(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalElements(res.data.totalElements || 0);
    } catch (e) {
      setError('Failed to load billing records. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [month, year, source]);

  useEffect(() => {
    fetchInvoices(search, activeTab, page);
  }, [fetchInvoices, activeTab, page]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      fetchInvoices(val, activeTab, 0);
    }, 400);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(0);
    setExpandedRows(new Set());
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ROW_ACTION = {
    DRAFT:     { label: 'Mark Generated', toStatus: 'GENERATED', color: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
    GENERATED: { label: 'Mark Sent',      toStatus: 'SENT',      color: 'border-blue-300 text-blue-700 hover:bg-blue-50'   },
    SENT:      { label: 'Mark Paid',       toStatus: 'PAID',      color: 'border-green-300 text-green-700 hover:bg-green-50'},
  };

  const handleRowAction = async (e, invoiceId, toStatus) => {
    e.stopPropagation();
    setMarkingId(invoiceId);
    try {
      await invoiceService.updateStatus(invoiceId, toStatus);
      setInvoices(prev => prev.map(inv =>
        inv.id === invoiceId ? { ...inv, status: toStatus } : inv
      ));
      if (onRefresh) onRefresh();
    } catch {
      // silently fail — user can retry via detail page
    } finally {
      setMarkingId(null);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || !month || !year) return;
    setShowBulkConfirm(false);
    setBulkLoading(true);
    try {
      await invoiceService.bulkUpdateStatus(month, year, bulkAction.fromStatus, bulkAction.toStatus);
      await fetchInvoices(search, activeTab, page);
      if (onRefresh) onRefresh();
    } catch {
      // silently fail
    } finally {
      setBulkLoading(false);
    }
  };

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <p className="text-gray-500 text-sm mb-3">{error}</p>
        <button
          onClick={() => fetchInvoices(search, activeTab, page)}
          className="text-sm text-primary border border-primary px-4 py-1.5 rounded-lg hover:bg-primary/5">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-gray-700">Enterprise Billing</p>
          <p className="text-xs text-gray-400">{totalElements} billing records</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bulkAction && (
            <button
              onClick={() => setShowBulkConfirm(true)}
              disabled={bulkLoading || totalElements === 0}
              className={`px-3 py-1.5 text-xs border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 font-medium ${bulkAction.color}`}>
              {bulkLoading && (
                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {bulkAction.label}
            </button>
          )}
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search enterprise..."
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex border-b border-gray-100 px-5 gap-6">
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-0">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-12 border-b border-gray-50 px-5 flex items-center gap-4">
              <div className="h-3 bg-gray-100 rounded animate-pulse flex-1" />
              <div className="h-3 bg-gray-100 rounded animate-pulse w-16" />
              <div className="h-3 bg-gray-100 rounded animate-pulse w-20" />
            </div>
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">
          No billing records found for this period.
        </div>
      ) : (
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                <th className="w-8 px-3 py-3" />
                <th className="text-left px-4 py-3 font-medium">Enterprise</th>
                <th className="text-right px-3 py-3 font-medium">Makers</th>
                <th className="text-right px-3 py-3 font-medium">Admins</th>
                <th className="text-right px-3 py-3 font-medium">Load Amt</th>
                <th className="text-right px-3 py-3 font-medium">UPI Txns</th>
                <th className="text-right px-3 py-3 font-medium">Maker Chg</th>
                <th className="text-right px-3 py-3 font-medium">Admin Chg</th>
                <th className="text-right px-4 py-3 font-medium">Total</th>
                <th className="text-center px-3 py-3 font-medium">Status</th>
                <th className="text-center px-3 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <React.Fragment key={inv.id}>
                  <tr
                    className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(inv.id)}>
                    <td className="px-3 py-3 text-center text-gray-400 text-xs">
                      {expandedRows.has(inv.id) ? '▼' : '▶'}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">
                        {inv.enterpriseName || `Enterprise #${inv.enterpriseId}`}
                      </p>
                      <p className="text-xs text-gray-400 font-mono">{inv.invoiceNumber}</p>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-600">{inv.makerCount ?? 0}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{inv.adminCount ?? 0}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(inv.totalLoadAmount)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{inv.upiTxnCount ?? 0}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(inv.makerCharge)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(inv.adminCharge)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatCurrency(inv.totalAmount)}</td>
                    <td className="px-3 py-3 text-center">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                      {ROW_ACTION[inv.status] && (
                        markingId === inv.id ? (
                          <span className="inline-flex items-center justify-center">
                            <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          </span>
                        ) : (
                          <button
                            onClick={(e) => handleRowAction(e, inv.id, ROW_ACTION[inv.status].toStatus)}
                            className={`text-xs px-2 py-1 border rounded transition-colors whitespace-nowrap ${ROW_ACTION[inv.status].color}`}>
                            {ROW_ACTION[inv.status].label}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                  {expandedRows.has(inv.id) && (
                    <tr className="border-t border-gray-100">
                      <td colSpan={11} className="p-0">
                        <InvoiceLineItems invoice={inv} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Page {page + 1} of {totalPages} &middot; {totalElements} billing records
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk action confirm dialog */}
      {showBulkConfirm && bulkAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-2">{bulkAction.label}?</h3>
            <p className="text-sm text-gray-500 mb-5">
              All <span className="font-medium">{bulkAction.fromStatus}</span> billing records for this month
              will be moved to <span className="font-medium">{bulkAction.toStatus}</span>.
              The page will reload after the update. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBulkConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleBulkAction}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-light">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseTable;
